import {
  type GetCheckoutLinksListFilters,
  type GetCheckoutLinksListItem,
  useGetCheckoutLinksList,
} from "@birdwatch/client";
import { useMemo } from "react";

import { isPresaleCheckoutLinkKey, PRESALE_CAMPAIGN } from "./presale-campaign";

/** Comfortably above the campaign's handful of links, so one page always holds them all. */
const LINK_PAGE_SIZE = 100;

/**
 * The campaign's `CheckoutLink` rows — the offers it sells, unpriced.
 *
 * The single owner of this query, so the funnel's priced offers (`usePresaleOffers`) and
 * the resume check (`usePresaleResume`) share one request and one cache entry instead of
 * each fetching the catalog. Rows arrive already narrowed to the campaign's — the caller
 * is not asked to re-apply the market-token rule.
 *
 * `search` matters: `CheckoutLink.key` is searchable, and the list's `transform` resolves
 * a `Product` plus a cached Stripe price for EVERY line item of EVERY row it returns, so
 * asking for the campaign's handful of links rather than the first 100 of the catalog is
 * the difference between a cheap request and an expensive one. It is a fuzzy name-or-key
 * search, so it returns a superset — `isPresaleCheckoutLinkKey` below is what enforces the
 * rule.
 */
export function useCampaignCheckoutLinks(options?: { enabled?: boolean }): {
  links: GetCheckoutLinksListItem[];
  isLoading: boolean;
  isError: boolean;
} {
  // FilterOperator and the generated FilterValue index-signature target are the same
  // wire shape; the named-interface-vs-index-signature mismatch needs the cast.
  const activeOnly: Record<string, unknown> = { active: { eq: true } };
  const query = useGetCheckoutLinksList()
    .filters(activeOnly as Partial<GetCheckoutLinksListFilters>)
    .search(PRESALE_CAMPAIGN.checkoutLinkKeyToken)
    .sort("key")
    .take(LINK_PAGE_SIZE)
    .get({ enabled: options?.enabled ?? true });

  const links = useMemo(
    () =>
      (query.data ?? []).filter((link) =>
        isPresaleCheckoutLinkKey(link.key, PRESALE_CAMPAIGN),
      ),
    [query.data],
  );

  return { links, isLoading: query.isLoading, isError: query.isError };
}
